import { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import Loading from "../../components/utils/Loading";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { gsap } from "gsap";
import { Modal } from "reactstrap";
import { loadModel, createSkipWithAlert } from "./DigitalTwinModel";
import PageHeader from "../../components/PageHeader";
import ContainerDetailsPanel from "./ContainerDetailsPanel";
import AnnotationDetailsPanel from "./AnnotationDetailsPanel";
import AnnotationForm from "./AnnotationForm";

const skipsConfig = [
  {
    modelPath: "./glbFiles/Skip30m3.glb",
    position: { x: 0.8, y: 0.75, z: 0.2 },
    scale: { x: 0.01, y: 0.01, z: 0.01 },
    rotation: { x: 0, y: (1.15 * Math.PI) / 2, z: 0 },
    dimensions: { length: 3.0, width: 2.0, height: 1.5 },
  },
  {
    modelPath: "./glbFiles/Skip30m3.glb",
    position: { x: -1.6, y: 0.75, z: 1.8 },
    scale: { x: 0.01, y: 0.01, z: 0.01 },
    rotation: { x: 0, y: (1.15 * Math.PI) / 2, z: 0 },
    dimensions: { length: 3.0, width: 2.0, height: 1.5 },
  },
  {
    modelPath: "./glbFiles/Skip20m3.glb",
    position: { x: -3.8, y: 0.6, z: 3.4 },
    scale: { x: 0.01, y: 0.01, z: 0.01 },
    rotation: { x: 0, y: (2.15 * Math.PI) / 2, z: 0 },
    dimensions: { length: 2.5, width: 1.8, height: 1.2 },
  },
];

const DigitalTwin = () => {
  const token = Cookies.get("userToken");

  const skipModelsRef = useRef([]);
  const alertSkipModelsRef = useRef([]);

  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState("isometric"); // État pour stocker la vue actuelle
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [containerAlertStates, setContainerAlertStates] = useState(
    Array(skipsConfig.length).fill(false),
  );

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedContainerIndex, setSelectedContainerIndex] = useState(null);
  const [annotationContextMenu, setAnnotationContextMenu] = useState(null);
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState(null);
  const [annotationModalIsOpen, setAnnotationModalIsOpen] = useState(false);
  const [interactivePoints, setInteractivePoints] = useState([]);
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [newCommentPosition, setNewCommentPosition] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [containerData, setContainerData] = useState(null);
  const [showAnnotationsPanel, setShowAnnotationsPanel] = useState(false);
  const [annotationsVisible, setAnnotationsVisible] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [showAnnotationForm, setShowAnnotationForm] = useState(false);
  const [selectedAnnotationPosition, setSelectedAnnotationPosition] =
    useState(null);
  const annotationModeRef = useRef(false);
  const annotationsRef = useRef([]);

  const interactivePointsRef = useRef([]);
  const annotationMarkersRef = useRef([]);
  const sceneRef = useRef(null);
  const mountRef = useRef(null);

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    const loadContainerData = () => {
      try {
        const stored = localStorage.getItem("digitalTwinContainerData");
        if (stored) {
          setContainerData(JSON.parse(stored));
        } else {
          const defaultData = {
            containers: skipsConfig.map((_, i) => ({
              id: i,
              comments: [],
              images: [],
              containerId: "",
            })),
          };
          setContainerData(defaultData);
          localStorage.setItem(
            "digitalTwinContainerData",
            JSON.stringify(defaultData),
          );
        }
      } catch (error) {
        console.error("Error loading container data:", error);
        const defaultData = {
          containers: skipsConfig.map((_, i) => ({
            id: i,
            comments: [],
            images: [],
            containerId: "",
          })),
        };
        setContainerData(defaultData);
      }
    };
    loadContainerData();

    const loadAnnotations = () => {
      try {
        const stored = localStorage.getItem("digitalTwinAnnotations");
        if (stored) {
          setAnnotations(JSON.parse(stored));
        }
      } catch (error) {
        console.error("Error loading annotations:", error);
      }
    };
    loadAnnotations();
  }, []);

  const saveContainerData = (data) => {
    try {
      localStorage.setItem("digitalTwinContainerData", JSON.stringify(data));
      setContainerData(data);
    } catch (error) {
      console.error("Error saving container data:", error);
    }
  };

  useEffect(() => {
    const mount = mountRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5); // Gainsboro
    sceneRef.current = scene;

    const initModels = async () => {
      try {
        // Configuration des modèles d'alerte
        const alertConfig = {
          alertPath: "./glbFiles/Danger.glb",
          alertScale: { x: 0.1, y: 0.1, z: 0.1 },
        };

        // Créer les skips avec leurs modèles d'alerte associés
        const loadAllSkips = async () => {
          for (const skipConfig of skipsConfig) {
            await createSkipWithAlert(
              skipConfig,
              alertConfig,
              scene,
              skipModelsRef,
              alertSkipModelsRef,
            );
          }
        };

        await loadAllSkips();

        const foxModel = await loadModel(
          "./glbFiles/SiteReze.glb",
          { x: 4, y: 0, z: -8 },
          { x: 0.01, y: 0.01, z: 0.01 },
          { x: 0, y: 1.8, z: 0 },
        );
        scene.add(foxModel);
      } catch (error) {
        console.error("Error loading models:", error);
      }
    };

    initModels();

    // Raycaster and mouse
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onMouseMove = (event) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      let isHovering = false;

      skipModelsRef.current.forEach((model) => {
        model.traverse((child) => {
          if (child.isMesh) {
            child.material.opacity = 1;
          }
        });

        if (
          intersects.length > 0 &&
          intersects.some((intersect) => intersect.object.parent === model)
        ) {
          model.traverse((child) => {
            if (child.isMesh) {
              isHovering = true;
              child.material.opacity = 0.3;
            }
          });
        }
      });

      // Change the cursor to pointer if hovering over a skip model or in annotation mode
      if (isHovering) {
        document.body.style.cursor = "pointer";
      } else if (annotationModeRef.current) {
        document.body.style.cursor = "crosshair";
      } else {
        document.body.style.cursor = "default";
      }
    };

    const onMouseClick = (event) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (annotationModeRef.current && intersects.length > 0) {
        const firstIntersect = intersects[0];
        const worldPosition = firstIntersect.point.clone();
        setSelectedAnnotationPosition(worldPosition);
        setShowAnnotationForm(true);
        setContextMenu(null);
        return;
      }

      if (intersects.length > 0) {
        const firstIntersect = intersects[0];

        const clickedComment = firstIntersect.object.userData?.comment;
        if (clickedComment) {
          setSelectedComment(clickedComment);
          setShowCommentModal(true);
          setContextMenu(null);
          return;
        }

        const clickedAnnotation = firstIntersect.object.userData?.annotation;
        if (clickedAnnotation) {
          const annotationIndex = annotationsRef.current.findIndex(
            (ann) => ann.id === clickedAnnotation.id,
          );
          if (annotationIndex !== -1) {
            setSelectedAnnotationIndex(annotationIndex);
            setAnnotationContextMenu({
              x: event.clientX,
              y: event.clientY,
            });
            setContextMenu(null);
          }
          return;
        }

        const clickedContainerIndex = skipModelsRef.current.findIndex(
          (model) => firstIntersect.object.parent === model,
        );

        if (clickedContainerIndex !== -1) {
          setSelectedContainerIndex(clickedContainerIndex);
          setContextMenu({
            x: event.clientX,
            y: event.clientY,
          });
          setAnnotationContextMenu(null);
        } else {
          setContextMenu(null);
        }
      } else {
        setContextMenu(null);
        setAnnotationContextMenu(null);
      }
    };

    mount.addEventListener("mousemove", onMouseMove);
    mount.addEventListener("click", onMouseClick);

    // Définir la caméra orthographique pour une vue isométrique
    const aspect = mount.clientWidth / mount.clientHeight;
    const camera = new THREE.OrthographicCamera(
      -aspect * 5,
      aspect * 5,
      5,
      -5,
      0.1,
      1000,
    );
    //camera.position.set(200, 20, 20);
    camera.lookAt(0, 0, 0);

    // Créer le renderer et activer les ombres
    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.shadowMap.enabled = true;
    mount.appendChild(renderer.domElement);

    // Lumière ambiante
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);

    // Ajouter une lumière directionnelle avec ombre
    const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(5, 5, 5);
    light.castShadow = true; // La lumière peut projeter des ombres
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    light.shadow.camera.near = 0.1;
    light.shadow.camera.far = 100;
    scene.add(light);

    // Fonction d'animation
    const animate = () => {
      //benne2.rotation.x += 0.01;
      //scene.rotation.y += 0.0004;
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };

    animate();

    // Ajouter les contrôles de la caméra
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // Permettre l'inertie
    controls.dampingFactor = 0.25; // Facteur d'amortissement
    controls.enableZoom = true; // Désactiver le zoom
    controls.enablePan = true; // Désactiver le panoramique

    // Fonction pour mettre à jour la vue
    const updateView = (view) => {
      let targetPosition;
      switch (view) {
        case "top":
          targetPosition = { x: 0, y: 10, z: 0 };
          break;
        case "isometric":
          targetPosition = { x: 10, y: 5, z: 5 };
          break;
        default:
          targetPosition = { x: 20, y: 5, z: 5 };
      }
      gsap.to(camera.position, {
        x: targetPosition.x,
        y: targetPosition.y,
        z: targetPosition.z,
        duration: 5,
        onUpdate: () => {
          camera.lookAt(0, 0, 0);
        },
      });
    };

    // Appliquer la vue initiale
    updateView(view);

    // Mettre à jour la vue lorsque l'état change
    const unsubscribe = () => {
      updateView(view);
    };
    unsubscribe();

    // Ajuster le rendu lors du redimensionnement de la fenêtre
    const handleResize = () => {
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      const aspect = mount.clientWidth / mount.clientHeight;
      camera.left = -aspect * 5;
      camera.right = aspect * 5;
      camera.top = 5;
      camera.bottom = -5;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    setIsLoading(false);

    return () => {
      window.removeEventListener("resize", handleResize);
      mount.removeChild(renderer.domElement);
    };
  }, [view]);

  useEffect(() => {
    if (!sceneRef.current) return;

    const getAnnotationColor = (type) => {
      const colors = {
        comment: 0x3b82f6,
        alert: 0xef4444,
        photo: 0x10b981,
        info: 0x8b5cf6,
      };
      return colors[type] || 0x6b7280;
    };

    const createAnnotationMarker = (annotation) => {
      const geometry = new THREE.SphereGeometry(0.08, 32, 32);
      const material = new THREE.MeshStandardMaterial({
        color: getAnnotationColor(annotation.type),
        transparent: true,
        opacity: annotationsVisible ? 0.9 : 0.4,
        metalness: 0.3,
        roughness: 0.2,
      });
      const marker = new THREE.Mesh(geometry, material);
      marker.position.set(
        annotation.position.x,
        annotation.position.y,
        annotation.position.z + 0.15,
      );
      marker.userData = { annotation };
      marker.visible = annotationsVisible;
      marker.castShadow = true;
      marker.receiveShadow = true;
      sceneRef.current.add(marker);
      annotationMarkersRef.current.push(marker);
    };

    annotationMarkersRef.current.forEach((marker) => {
      sceneRef.current.remove(marker);
    });
    annotationMarkersRef.current = [];

    annotations.forEach((annotation) => {
      createAnnotationMarker(annotation);
    });
  }, [annotations, annotationsVisible]);

  useEffect(() => {
    if (!sceneRef.current) return;
    annotationMarkersRef.current.forEach((marker) => {
      if (marker) {
        marker.visible = annotationsVisible;
        if (marker.material) {
          marker.material.opacity = annotationsVisible ? 0.9 : 0.4;
        }
      }
    });
  }, [annotationsVisible]);

  useEffect(() => {
    if (!sceneRef.current) return;

    const createInteractivePoint = (point, sceneInstance) => {
      const geometry = new THREE.SphereGeometry(0.15, 16, 16);

      const typeColors = {
        USER_FEEDBACK_TRI: 0x3b82f6,
        USER_FEEDBACK_TRAVAUX: 0x2563eb,
        AGENT_FEEDBACK_TRI: 0x10b981,
        AGENT_FEEDBACK_TRAVAUX: 0x059669,
        AUTO_ALERT: 0xef4444,
        MAINTENANCE: 0xf97316,
        QUALITY: 0x8b5cf6,
        ENVIRONMENT: 0x16a34a,
        SECURITY: 0xdc2626,
      };

      const material = new THREE.MeshBasicMaterial({
        color: typeColors[point.type] || 0x6b7280,
        transparent: true,
        opacity: 0.8,
      });

      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(
        point.position.x,
        point.position.y + 0.5,
        point.position.z,
      );
      sphere.userData = { commentId: point.id, comment: point };
      sceneInstance.add(sphere);
      interactivePointsRef.current.push(sphere);

      const edges = new THREE.EdgesGeometry(geometry);
      const line = new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: 0x000000, opacity: 0.5 }),
      );
      sphere.add(line);
    };

    interactivePointsRef.current.forEach((point) => {
      sceneRef.current.remove(point);
    });
    interactivePointsRef.current = [];

    interactivePoints.forEach((point) => {
      createInteractivePoint(point, sceneRef.current);
    });
  }, [interactivePoints]);

  const setSkipColor = (index, color) => {
    if (skipModelsRef.current[index]) {
      skipModelsRef.current[index].traverse((child) => {
        if (child.isMesh) {
          child.material.color.set(color);
        }
      });
    }
  };

  const skipAlertMode = (index, mode) => {
    if (mode === "on") {
      // Changer la couleur du skip en rouge
      setSkipColor(index, 0xff0000);

      // Activer le modèle d'alerte et démarrer l'animation de rotation
      if (alertSkipModelsRef.current[index]) {
        alertSkipModelsRef.current[index].visible =
          skipModelsRef.current[index].visible;

        // Démarrer l'animation de rotation pour le modèle d'alerte
        gsap.to(alertSkipModelsRef.current[index].rotation, {
          y: "+=6.28319", // 2*PI pour une rotation complète
          duration: 5,
          repeat: -1,
          ease: "linear",
        });
      }

      // Update the container state
      const newStates = [...containerAlertStates];
      newStates[index] = true;
      setContainerAlertStates(newStates);
    } else if (mode === "off") {
      // Réinitialiser la couleur du skip à gris
      setSkipColor(index, 0xeeeeee);

      // Rendre le modèle d'alerte invisible
      if (alertSkipModelsRef.current[index]) {
        alertSkipModelsRef.current[index].visible = false;
      }

      // Arrêter l'animation de rotation pour le modèle d'alerte
      if (alertSkipModelsRef.current[index]) {
        gsap.killTweensOf(alertSkipModelsRef.current[index].rotation);
      }

      // Update the container state
      const newStates = [...containerAlertStates];
      newStates[index] = false;
      setContainerAlertStates(newStates);
    }
  };

  const toggleModelVisibility = (index) => {
    if (skipModelsRef.current[index]) {
      skipModelsRef.current[index].visible =
        !skipModelsRef.current[index].visible;
    }

    if (
      alertSkipModelsRef.current[index] &&
      containerAlertStates[index] === true
    ) {
      alertSkipModelsRef.current[index].visible =
        !alertSkipModelsRef.current[index].visible;
    }
  };

  const handleContextMenuAction = (action) => {
    if (selectedContainerIndex === null) return;

    switch (action) {
      case "toggleAlert":
        const currentAlertState = containerAlertStates[selectedContainerIndex];
        skipAlertMode(selectedContainerIndex, currentAlertState ? "off" : "on");
        break;
      case "toggleVisibility":
        toggleModelVisibility(selectedContainerIndex);
        break;
      case "viewDetails":
        setAnnotationModalIsOpen(false);
        setModalIsOpen(true);
        break;
      case "addComment":
        setNewCommentPosition({
          containerIndex: selectedContainerIndex,
          position: skipsConfig[selectedContainerIndex].position,
        });
        setShowAddCommentModal(true);
        break;
      default:
        break;
    }
    setContextMenu(null);
  };

  const handleAnnotationContextMenuAction = (action) => {
    if (selectedAnnotationIndex === null) return;

    switch (action) {
      case "viewDetails":
        setModalIsOpen(false);
        setAnnotationModalIsOpen(true);
        break;
      case "addComment":
        setModalIsOpen(false);
        setAnnotationModalIsOpen(true);
        break;
      case "close":
        const updatedAnnotations = [...annotations];
        updatedAnnotations[selectedAnnotationIndex] = {
          ...updatedAnnotations[selectedAnnotationIndex],
          closed: true,
        };
        setAnnotations(updatedAnnotations);
        localStorage.setItem(
          "digitalTwinAnnotations",
          JSON.stringify(updatedAnnotations),
        );
        break;
      default:
        break;
    }
    setAnnotationContextMenu(null);
  };

  if (token) {
    return isLoading ? (
      <Loading color="#F5F5F5" />
    ) : (
      <div className="settings-page-wrapper">
        <PageHeader title="Digital Twin" />
        <div className="settings-content-wrapper relative">
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <button
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full transition-all ${
                showAnnotationsPanel
                  ? "bg-[var(--akanthas-cyan)] text-white shadow-md"
                  : "bg-white text-[var(--akanthas-cyan)] border border-[var(--akanthas-cyan)] hover:bg-gray-50"
              }`}
              onClick={() => {
                const newState = !showAnnotationsPanel;
                setShowAnnotationsPanel(newState);
                annotationModeRef.current = newState;
              }}
              title="Annoter"
            >
              <i className="fas fa-pencil-alt text-lg"></i>
              <span>Annoter</span>
            </button>
            <button
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full transition-all ${
                annotationsVisible
                  ? "bg-[var(--akanthas-cyan)] text-white shadow-md"
                  : "bg-white text-[var(--akanthas-cyan)] border border-[var(--akanthas-cyan)] hover:bg-gray-50"
              }`}
              onClick={() => setAnnotationsVisible(!annotationsVisible)}
              title="Voir les annotations"
            >
              <i className="fas fa-map-marker-alt text-lg"></i>
              <span>Annotations ({annotations.length})</span>
            </button>
            <button
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full transition-all ${
                view === "top"
                  ? "bg-[var(--akanthas-cyan)] text-white shadow-md"
                  : "bg-white text-[var(--akanthas-cyan)] border border-[var(--akanthas-cyan)] hover:bg-gray-50"
              }`}
              onClick={() => setView("top")}
              title="Vue de dessus"
            >
              <i className="fas fa-arrow-up text-lg"></i>
              <span>Vue de dessus</span>
            </button>
            <button
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full transition-all ${
                view === "isometric"
                  ? "bg-[var(--akanthas-cyan)] text-white shadow-md"
                  : "bg-white text-[var(--akanthas-cyan)] border border-[var(--akanthas-cyan)] hover:bg-gray-50"
              }`}
              onClick={() => setView("isometric")}
              title="Vue isométrique"
            >
              <i className="fas fa-cubes text-lg"></i>
              <span>Vue isométrique</span>
            </button>
          </div>
          <div ref={mountRef} style={{ width: "100%", height: "100vh" }} />

          {showAnnotationsPanel && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-100 border-2 border-blue-400 rounded-lg px-4 py-2 z-10">
              <p className="text-sm font-medium text-blue-800">
                <i className="fas fa-info-circle mr-2"></i>
                Mode annotation actif - Cliquez sur la carte pour ajouter une
                annotation
              </p>
            </div>
          )}

          {showAnnotationForm && (
            <AnnotationForm
              position={selectedAnnotationPosition}
              onSave={(annotation) => {
                const updated = [...annotations, annotation];
                setAnnotations(updated);
                localStorage.setItem(
                  "digitalTwinAnnotations",
                  JSON.stringify(updated),
                );
                setShowAnnotationForm(false);
                setSelectedAnnotationPosition(null);
              }}
              onCancel={() => {
                setShowAnnotationForm(false);
                setSelectedAnnotationPosition(null);
              }}
            />
          )}

          {contextMenu && (
            <div
              className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20 min-w-[200px]"
              style={{
                left: `${contextMenu.x}px`,
                top: `${contextMenu.y}px`,
              }}
            >
              <div className="px-4 py-2 border-b border-gray-200 mb-1">
                <h3 className="font-semibold text-gray-800">Conteneur</h3>
              </div>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                onClick={() => handleContextMenuAction("toggleAlert")}
              >
                <i
                  className={`fas ${
                    containerAlertStates[selectedContainerIndex]
                      ? "fa-bell-slash text-gray-500"
                      : "fa-bell text-yellow-500"
                  }`}
                ></i>
                <span>
                  {containerAlertStates[selectedContainerIndex]
                    ? "Désactiver alerte"
                    : "Activer alerte"}
                </span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                onClick={() => handleContextMenuAction("toggleVisibility")}
              >
                <i className="fas fa-eye text-[var(--akanthas-cyan)]"></i>
                <span>Afficher/Masquer</span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                onClick={() => handleContextMenuAction("viewDetails")}
              >
                <i className="fas fa-info-circle text-[var(--akanthas-cyan)]"></i>
                <span>Voir détails</span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-200 mt-1 pt-2"
                onClick={() => handleContextMenuAction("addComment")}
              >
                <i className="fas fa-comment text-[var(--akanthas-cyan)]"></i>
                <span>Ajouter commentaire</span>
              </button>
            </div>
          )}

          {annotationContextMenu && (
            <div
              className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20 min-w-[200px]"
              style={{
                left: `${annotationContextMenu.x}px`,
                top: `${annotationContextMenu.y}px`,
              }}
            >
              <div className="px-4 py-2 border-b border-gray-200 mb-1">
                <h3 className="font-semibold text-[var(--akanthas-red)]">
                  Signalement
                </h3>
              </div>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                onClick={() => handleAnnotationContextMenuAction("viewDetails")}
              >
                <i className="fas fa-info-circle text-[var(--akanthas-red)]"></i>
                <span>Voir détails</span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-200 mt-1 pt-2"
                onClick={() => handleAnnotationContextMenuAction("addComment")}
              >
                <i className="fas fa-comment text-[var(--akanthas-red)]"></i>
                <span>Ajouter commentaire</span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-200 mt-1 pt-2"
                onClick={() => handleAnnotationContextMenuAction("close")}
              >
                <i className="fas fa-check-circle text-[var(--akanthas-red)]"></i>
                <span>Clôturer</span>
              </button>
            </div>
          )}

          <ContainerDetailsPanel
            isOpen={modalIsOpen}
            onClose={() => {
              setModalIsOpen(false);
              setAnnotationModalIsOpen(false);
            }}
            containerIndex={selectedContainerIndex}
            containerConfig={
              selectedContainerIndex !== null
                ? skipsConfig[selectedContainerIndex]
                : null
            }
            alertState={
              selectedContainerIndex !== null
                ? containerAlertStates[selectedContainerIndex]
                : false
            }
            visibility={
              selectedContainerIndex !== null
                ? skipModelsRef.current[selectedContainerIndex]?.visible
                : true
            }
            containerData={containerData?.containers?.[selectedContainerIndex]}
            onSaveData={(data) => {
              const updated = { ...containerData };
              if (!updated.containers) {
                updated.containers = [];
              }
              updated.containers[selectedContainerIndex] = {
                ...updated.containers[selectedContainerIndex],
                ...data,
              };
              saveContainerData(updated);
            }}
          />

          <AnnotationDetailsPanel
            isOpen={annotationModalIsOpen}
            onClose={() => {
              setAnnotationModalIsOpen(false);
              setModalIsOpen(false);
            }}
            annotationIndex={selectedAnnotationIndex}
            annotation={
              selectedAnnotationIndex !== null
                ? annotations[selectedAnnotationIndex]
                : null
            }
            onSaveData={(data) => {
              const updated = [...annotations];
              updated[selectedAnnotationIndex] = data;
              setAnnotations(updated);
              localStorage.setItem(
                "digitalTwinAnnotations",
                JSON.stringify(updated),
              );
            }}
          />

          <Modal
            isOpen={showAddCommentModal}
            toggle={() => setShowAddCommentModal(false)}
            contentLabel="Add Comment Modal"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Ajouter un commentaire
                </h2>
                <button
                  onClick={() => setShowAddCommentModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <AddCommentForm
                position={newCommentPosition}
                onSave={(comment) => {
                  setInteractivePoints([...interactivePoints, comment]);
                  setShowAddCommentModal(false);
                  setNewCommentPosition(null);
                }}
                onCancel={() => {
                  setShowAddCommentModal(false);
                  setNewCommentPosition(null);
                }}
              />
            </div>
          </Modal>

          <Modal
            isOpen={showCommentModal}
            toggle={() => setShowCommentModal(false)}
            contentLabel="Comment Details Modal"
          >
            {selectedComment && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Détails du commentaire
                  </h2>
                  <button
                    onClick={() => setShowCommentModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Type</p>
                    <p className="font-medium">
                      {selectedComment.type?.startsWith("USER_") ||
                      selectedComment.type?.startsWith("AGENT_")
                        ? getCommentTypeLabel(selectedComment.type)
                        : selectedComment.type?.charAt(0).toUpperCase() +
                            selectedComment.type?.slice(1) || "Annotation"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Auteur</p>
                    <p className="font-medium">
                      {selectedComment.author || "Anonyme"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date</p>
                    <p className="text-sm">
                      {new Date(
                        selectedComment.date || selectedComment.createdAt,
                      ).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  {selectedComment.image && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Photo</p>
                      <img
                        src={selectedComment.image}
                        alt="Annotation"
                        className="w-full max-h-64 object-contain rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                  {selectedComment.text && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Commentaire</p>
                      <p className="text-sm bg-gray-50 p-3 rounded-md">
                        {selectedComment.text}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={() => {
                        if (selectedComment.position) {
                          const updated = annotations.filter(
                            (a) => a.id !== selectedComment.id,
                          );
                          setAnnotations(updated);
                          localStorage.setItem(
                            "digitalTwinAnnotations",
                            JSON.stringify(updated),
                          );
                        } else {
                          setInteractivePoints(
                            interactivePoints.filter(
                              (p) => p.id !== selectedComment.id,
                            ),
                          );
                        }
                        setShowCommentModal(false);
                        setSelectedComment(null);
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      <i className="fas fa-trash mr-2"></i>
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </div>
    );
  }
};

const getCommentTypeLabel = (type) => {
  const labels = {
    USER_FEEDBACK_TRI: "Retour usager - Tri",
    USER_FEEDBACK_TRAVAUX: "Retour usager - Travaux",
    AGENT_FEEDBACK_TRI: "Retour agent - Tri",
    AGENT_FEEDBACK_TRAVAUX: "Retour agent - Travaux",
    AUTO_ALERT: "Alerte automatique",
    MAINTENANCE: "Maintenance",
    QUALITY: "Qualité",
    ENVIRONMENT: "Environnement",
    SECURITY: "Sécurité",
  };
  return labels[type] || type;
};

const AddCommentForm = ({ position, onSave, onCancel }) => {
  const [commentType, setCommentType] = useState("USER_FEEDBACK_TRI");
  const [commentText, setCommentText] = useState("");
  const [author, setAuthor] = useState("");

  const commentTypes = [
    {
      value: "USER_FEEDBACK_TRI",
      label: "Retour usager - Tri",
      icon: "fa-user",
      color: "text-blue-500",
    },
    {
      value: "USER_FEEDBACK_TRAVAUX",
      label: "Retour usager - Travaux",
      icon: "fa-user",
      color: "text-blue-500",
    },
    {
      value: "AGENT_FEEDBACK_TRI",
      label: "Retour agent - Tri",
      icon: "fa-user-tie",
      color: "text-green-500",
    },
    {
      value: "AGENT_FEEDBACK_TRAVAUX",
      label: "Retour agent - Travaux",
      icon: "fa-user-tie",
      color: "text-green-500",
    },
    {
      value: "AUTO_ALERT",
      label: "Alerte automatique",
      icon: "fa-exclamation-triangle",
      color: "text-red-500",
    },
    {
      value: "MAINTENANCE",
      label: "Maintenance",
      icon: "fa-tools",
      color: "text-orange-500",
    },
    {
      value: "QUALITY",
      label: "Qualité",
      icon: "fa-chart-line",
      color: "text-purple-500",
    },
    {
      value: "ENVIRONMENT",
      label: "Environnement",
      icon: "fa-leaf",
      color: "text-green-600",
    },
    {
      value: "SECURITY",
      label: "Sécurité",
      icon: "fa-shield-alt",
      color: "text-red-600",
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    const newComment = {
      id: Date.now(),
      type: commentType,
      text: commentText,
      author: author || "Anonyme",
      position: position?.position || { x: 0, y: 0, z: 0 },
      containerIndex: position?.containerIndex,
      createdAt: new Date().toISOString(),
    };

    onSave(newComment);
    setCommentText("");
    setAuthor("");
    setCommentType("USER_FEEDBACK_TRI");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type de commentaire
        </label>
        <select
          value={commentType}
          onChange={(e) => setCommentType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)]"
        >
          {commentTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Auteur (optionnel)
        </label>
        <input
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Nom de l'auteur"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Commentaire *
        </label>
        <textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Saisissez votre commentaire..."
          rows={4}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)]"
        />
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-[var(--akanthas-cyan)] text-white rounded-md hover:bg-[var(--akanthas-yellow)]"
        >
          Enregistrer
        </button>
      </div>
    </form>
  );
};

export default DigitalTwin;
